import { useEffect } from "react";
import { theme } from "@/styles/theme";
import { fluid } from "@/styles/utils/fluid";
import { HeadSEO } from "@/components/SEO/HeadSEO";
import EmailSignup from "@/components/EmailSignup";
import AccordionHighlightsContainer from "@/components/AccordionHighlights/AccordionHighlightsContainer";
import * as StyledLayout from "@/layouts/Styled.Layout";
import * as StyledTypography from "@/styles/Styled.Typography";
import * as StyledHome from "./Styled.Home";
import hero1720 from "@/assets/images/website-header-bg-1720.webp";
import hero1136 from "@/assets/images/website-header-bg-1136.webp";
import hero852 from "@/assets/images/website-header-bg-852.webp";
import hero568 from "@/assets/images/website-header-bg-568.webp";
import hero384 from "@/assets/images/website-header-bg-384.webp";
import profile1800 from "@/assets/images/sy-profile-shot-1800.webp";
import profile1200 from "@/assets/images/sy-profile-shot-1200.webp";
import profile900 from "@/assets/images/sy-profile-shot-900.webp";
import profile600 from "@/assets/images/sy-profile-shot-600.webp";
import profile400 from "@/assets/images/sy-profile-shot-400.webp";

export default function Home() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Stanley Yeo",
    url: "https://stanleyyeo.com",
  };

  useEffect(() => {
    const clampStr = fluid({
      minSize: theme.fontScale.perfectFourth.size,
      maxSize: theme.fontScale.augmentedFourth.size,
      minVW: theme.breakpointValues.lg,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    });

    console.log("[fluid] clamp:", clampStr);
    console.log(
      "[fluid] root font-size:",
      getComputedStyle(document.documentElement).fontSize
    );
  }, [theme]);

  return (
    <>
      <HeadSEO
        siteTitle="Home"
        siteDescription="Independent web designer in Singapore helping small businesses build fast WordPress sites, strong brands, and lasting online presence."
        structuredData={structuredData}
        // preloadImage={hero1720} // or the main src for hero
        // preloadImageSrcSet={`
        //   ${hero384} 384w,
        //   ${hero568} 568w,
        //   ${hero852} 852w,
        //   ${hero1136} 1136w,
        //   ${hero1720} 1720w
        // `}
        // preloadImageSizes="
        //   (max-width: 400px) 384px,
        //   (max-width: 600px) 568px,
        //   (max-width: 900px) 852px,
        //   (max-width: 1200px) 1136px,
        //   (max-width: 1800px) 1720px,
        //   100vw
        // "
      />
      <StyledTypography.ParadoxSubtitle as="h2" data-animate="fade-up">
        is to stop chasing, and start
        <StyledHome.CaveatWrapper>
          <StyledHome.CaveatSubtitle as="p" data-animate="fade-up">
            at the end...
          </StyledHome.CaveatSubtitle>
          attracting
        </StyledHome.CaveatWrapper>
      </StyledTypography.ParadoxSubtitle>
      <StyledHome.GridHomeIntro>
        <StyledHome.GridItemHomeIntro as="article">
          <p data-animate="fade-up">
            If you are still chasing after productivity, followers, work, or
            money, there’s a healthy chance you are getting everything
            backwards.
          </p>
          <p data-animate="fade-up">
            Once you can competently refrain your common sense from duping you
            into believing what is common, you are already halfway out of the
            crowd..
          </p>
          <p data-animate="fade-up">
            Do you know you already have everything to start winning?
          </p>
        </StyledHome.GridItemHomeIntro>
        <StyledHome.GridItemHomeIntro as="article">
          <div data-animate="fade-up">
            <EmailSignup />
          </div>
        </StyledHome.GridItemHomeIntro>
      </StyledHome.GridHomeIntro>
      <StyledLayout.SectionDivider />
      <StyledHome.GridHomeAbout as="article">
        <StyledHome.GridItemHomeAbout>
          <StyledHome.ImgWrapper>
            <StyledHome.HomeAboutHeading
              $sectionAboutH2={true}
              as="h2"
              data-animate="fade-up"
            >
              Hello,
              <br /> I'm Stan.
            </StyledHome.HomeAboutHeading>
            <StyledHome.ImgWrapperStyle data-animate="fade-up">
              <picture>
                <source
                  srcSet={`
                    ${profile400} 250w,
                    ${profile600} 350w,
                    ${profile900} 500w,
                    ${profile1200} 700w,
                    ${profile1800} 900w
                  `}
                  type="image/webp"
                />
                <img
                  src={profile1200}
                  srcSet={`
                    ${profile400} 250w,
                    ${profile600} 350w,
                    ${profile900} 500w,
                    ${profile1200} 700w,
                    ${profile1800} 900w
                  `}
                  sizes="(max-width: 400px) 250px,
                    (max-width: 600px) 350px,
                    (max-width: 900px) 500px,
                    (max-width: 1200px) 700px,
                    900px"
                  alt="Photo of Stanley Yeo"
                  fetchPriority="high"
                  decoding="async"
                  loading="lazy"
                />
              </picture>
            </StyledHome.ImgWrapperStyle>
          </StyledHome.ImgWrapper>
        </StyledHome.GridItemHomeAbout>
        <StyledHome.GridItemHomeAbout>
          <p data-animate="fade-up">
            I’m the creator of this website. Over here, we’ll explore a range of{" "}
            <StyledTypography.StyledLink to="/articles/">
              topics
            </StyledTypography.StyledLink>
            ,{" "}
            <StyledTypography.StyledLink to="/newsletter/">
              strategies
            </StyledTypography.StyledLink>
            , and{" "}
            <StyledTypography.StyledLink to="/resources/">
              tools
            </StyledTypography.StyledLink>{" "}
            that help you navigate and win your way personally and
            professionally as a creative designer (hopefully).
          </p>
          <p data-animate="fade-up">
            As far as I can tell, the definition of winning looks different to
            different people, and I don’t pretend I know yours. After all, who
            doesn’t want to excel at their craft, realise their full earning
            potential, and lead happier lives?
          </p>
          <p data-animate="fade-up">
            All I know for sure is that winning means gaining a little control
            over the things that matters to you. But let’s just assume you don’t
            win. Will you learn something today that contributes to being a
            better (different) version of yourself than yesterday? Read on and
            decide for yourself.
          </p>
        </StyledHome.GridItemHomeAbout>
      </StyledHome.GridHomeAbout>
      <StyledLayout.SectionDivider />
      <StyledTypography.SectionTitle as="h2">
        Navigate your way
        <StyledTypography.SectionTitleStroke>
          Navigate your way
        </StyledTypography.SectionTitleStroke>
      </StyledTypography.SectionTitle>
      <StyledHome.GridHomeHighlights data-animate="fade-up">
        <AccordionHighlightsContainer />
      </StyledHome.GridHomeHighlights>
    </>
  );
}
